import { Injectable } from '@angular/core';
import { DestinationsService, Jobsite, Order, OrderTypeCodesEnum } from '@cemex-core/cmx-api';
import { BusinessLine } from '@cemex-core/cmx-api/models/business-lines/business-line';
import { IShippingDestination } from '@cemex-core/cmx-api/models/destinations/shipping-destination';
import { IDestinationsRequest } from '@cemex-core/cmx-api/models/destinations/shipping-destinations-request';
import { IDestinationsResponse } from '@cemex-core/cmx-api/models/destinations/shipping-destinations-response';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { CmxHttpClient, getLegalEntity } from '@cemex/cmx-client-layout';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { getCustomerAndOneClickState } from '@ordersnproducts/modules/one-click-order/selectors/one-click-customer.selector';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap, debounceTime } from 'rxjs/operators';
import { ShippingConditionEnum } from '../../shared/constants/shipping-condition.enum';
import { OneClickLoadJobsiteStdLotAction, OneClickSelectJobsiteAction } from '../actions/one-click-jobsites.actions';
import {
  OneClickLoadPointOfDeliveriesAction,
  OneClickLoadPointOfDeliveriesCompleteAction,
  OneClickPointOfDeliveriesActionTypes,
  OneClickPreLoadPointOfDeliveriesAction,
  OneClickSelectPointOfDeliveriesAction,
} from '../actions/one-click-pods.actions';
import { OneClickLoadProductsAction } from '../actions/one-click-products.actions';
import {
  OneClickSelectShippingModeAction,
  OneClickShippingModeActionTypes,
} from '../actions/one-click-shipment-mode.actions';
import { OneClickProject } from '../models/one-click-project.model';
import { ShippingLocationModeCode } from '../models/shipping-location-mode.model';
import { State } from '../reducers';
import { getOneClickPreloadedOrder } from '../selectors/one-click-profiles.selector';

interface JobsiteParent {
  address: {
    addressId: number;
    links: {
      self: string;
    };
  };
  jobsiteCode: string;
  jobsiteDesc: string;
  jobsiteId: number;
  jobsiteStatus: {
    jobsiteStatusCode: string;
    jobsiteStatusDesc: string;
    jobsiteStatusId: number;
  };
  links: {
    contacts: string;
    pointOfDeliveries: string;
    pointofdeliveryandrequests: string;
    salesareas: string;
    self: string;
  };
}

@Injectable()
export class OneClickPointOfDeliveriesEffects {
  selectLocationMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneClickShippingModeActionTypes.SELECT),
        switchMap((action: OneClickSelectShippingModeAction) =>
          this.store.pipe(
            select(getOneClickPreloadedOrder),
            debounceTime(300),
            take(1),
            map((preloadedOrder) => {
              return { preloadedOrder, action };
            }),
          ),
        ),
        map(({ preloadedOrder, action }) => {
          if (action.payload.code === ShippingLocationModeCode.PointOfDelivery) {
            if (preloadedOrder) {
              this.store.dispatch(new OneClickPreLoadPointOfDeliveriesAction(preloadedOrder));
            } else {
              this.store.dispatch(new OneClickLoadPointOfDeliveriesAction());
            }
          }
        }),
      ),
    { dispatch: false },
  );

  loadPods$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickPointOfDeliveriesActionTypes.LOAD_PODS),
      switchMap(() => this.store.pipe(select(getCustomerAndOneClickState), take(1))),
      map((customerAndState) =>
        this.makeDestinationsRequest(customerAndState.legalEntity, customerAndState.state.businessLinesState.selected),
      ),
      switchMap((request: IDestinationsRequest) => this.destinationsService.getDestinationsV2(request)),
      map(
        (response: IDestinationsResponse) =>
          new OneClickLoadPointOfDeliveriesCompleteAction(response.shippingDestinations),
      ),
    ),
  );

  preloadPod$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(OneClickPointOfDeliveriesActionTypes.PRELOAD_POD),
      map((action: OneClickPreLoadPointOfDeliveriesAction) => {
        const pod = action.payload.pointOfDelivery;
        return {
          shippingDestinationCode: pod.pointOfDeliveryCode,
          shippingDestinationDesc: pod.pointOfDeliveryDesc,
          shippingDestinationId: pod.pointOfDeliveryId,
        } as IShippingDestination;
      }),
      map((pod) => new OneClickLoadPointOfDeliveriesCompleteAction([pod])),
    ),
  );

  loadParent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OneClickPointOfDeliveriesActionTypes.SELECT_POD),
        switchMap((action: OneClickSelectPointOfDeliveriesAction) => {
          return this.store.pipe(
            select(getOneClickPreloadedOrder),
            take(1),
            map((preloaded) => ({ preloaded, action })),
          );
        }),
        switchMap(({ preloaded, action }) => {
          if (preloaded) {
            return this.makeJobsiteFromPreloaded(preloaded);
          } else {
            return this.makeJobsiteFromParentLink(action);
          }
        }),
        tap((jobsite) => {
          this.store.dispatch(new OneClickSelectJobsiteAction(jobsite as OneClickProject));
          this.store.dispatch(new OneClickLoadJobsiteStdLotAction());
          this.store.dispatch(new OneClickLoadProductsAction());
        }),
      ),
    { dispatch: false },
  );

  private makeJobsiteFromParentLink(action: OneClickSelectPointOfDeliveriesAction) {
    return this.http.get<JobsiteParent>(action.payload.links.parent).pipe(
      switchMap((parent) => this.store.select(getLegalEntity).pipe(map((customer) => ({ parent, customer })))),
      map(({ parent, customer }) => this.parseParentJobsite(parent, customer)),
    );
  }

  private makeJobsiteFromPreloaded(preloaded: Order) {
    return this.store.pipe(
      select(getLegalEntity),
      take(1),
      map((customer) => this.parsePreloadedJobsite(preloaded.jobsite, customer)),
    );
  }

  private parseParentJobsite(parent: JobsiteParent, customer: ILegalEntity) {
    return {
      customer: {
        customerCode: customer.legalEntityCode,
        customerDesc: customer.legalEntityDesc,
        customerId: customer.legalEntityId,
      },
      jobsite: {
        jobsiteId: parent.jobsiteId,
        jobsiteDesc: parent.jobsiteDesc,
        jobsiteCode: parent.jobsiteCode,
      },
    };
  }

  private parsePreloadedJobsite(jobsite: Jobsite, customer: ILegalEntity) {
    return {
      customer: {
        customerCode: customer.legalEntityCode,
        customerDesc: customer.legalEntityDesc,
        customerId: customer.legalEntityId,
      },
      jobsite: {
        jobsiteId: jobsite.jobsiteId,
        jobsiteDesc: jobsite.jobsiteDesc,
        jobsiteCode: jobsite.jobsiteCode,
      },
    };
  }

  private makeDestinationsRequest(legalEntity: ILegalEntity, businessLine: BusinessLine): IDestinationsRequest {
    return {
      shippingConditionCode: ShippingConditionEnum.Standard,
      productLineCode: businessLine.productLineCode,
      customerCode: legalEntity.legalEntityTypeCode,
      commercialChannelCode: OrderTypeCodesEnum.OneClickToBuy,
    } as IDestinationsRequest;
  }

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private destinationsService: DestinationsService,
    private http: CmxHttpClient,
  ) {}
}
